import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import PageHeader from '../../components/PageHeader';
import LoadingSpinner from '../../components/LoadingSpinner';
import EmptyState from '../../components/EmptyState';
import {
  clientAnalyticsApi,
  type PerformanceTrendItem,
  type ComplianceBreakdown,
} from '../../services/analytics';
import { toGGScore, toGGDelta, getScoreColor, getScoreColorClass } from '../../utils/score';

// ---------- Constants ----------
const TOOLTIP_STYLE = {
  backgroundColor: '#1e293b',
  border: 'none',
  borderRadius: '8px',
  color: '#f8fafc',
  fontSize: '12px',
};

// ---------- Helpers ----------
function formatWeek(weekStart: string): string {
  const d = new Date(weekStart + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function scoreColor(score: number): string {
  return getScoreColorClass(getScoreColor(score));
}

function barColor(pct: number): string {
  if (pct >= 75) return 'bg-emerald-500';
  if (pct >= 50) return 'bg-amber-500';
  return 'bg-red-500';
}

function severityVariant(severity: string): string {
  switch (severity.toLowerCase()) {
    case 'critical':
      return 'bg-red-50 text-red-600';
    case 'high':
      return 'bg-orange-50 text-orange-600';
    case 'medium':
      return 'bg-amber-50 text-amber-600';
    case 'low':
      return 'bg-slate-100 text-slate-600';
    default:
      return 'bg-slate-100 text-slate-600';
  }
}

function categoryVariant(category: string): string {
  switch (category.toLowerCase()) {
    case 'attendance':
      return 'bg-blue-50 text-blue-600';
    case 'patrol':
      return 'bg-emerald-50 text-emerald-600';
    case 'incident':
      return 'bg-red-50 text-red-600';
    case 'reporting':
      return 'bg-purple-50 text-purple-600';
    default:
      return 'bg-slate-100 text-slate-600';
  }
}

// ---------- Transform performance trends for multi-line chart ----------
interface TrendChartRow {
  week: string;
  vendor: number | null;
  site: number | null;
  guard: number | null;
}

function transformTrends(raw: PerformanceTrendItem[]): TrendChartRow[] {
  const map = new Map<string, TrendChartRow>();
  for (const item of raw) {
    if (!map.has(item.week_start)) {
      map.set(item.week_start, { week: formatWeek(item.week_start), vendor: null, site: null, guard: null });
    }
    const row = map.get(item.week_start)!;
    if (item.entity_type === 'vendor') row.vendor = toGGScore(item.avg_score);
    else if (item.entity_type === 'site') row.site = toGGScore(item.avg_score);
    else if (item.entity_type === 'guard') row.guard = toGGScore(item.avg_score);
  }
  return Array.from(map.values());
}

// ---------- CSV Export ----------
function downloadCsv(filename: string, csvContent: string) {
  const blob = new Blob([csvContent], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}

// ---------- Build compliance metrics ----------
interface ComplianceMetric {
  label: string;
  value: number;
  comingSoon: boolean;
}

function buildComplianceMetrics(data: ComplianceBreakdown | null): ComplianceMetric[] {
  if (!data) return [];
  return [
    { label: 'Reporting Quality', value: data.detailed_notes_rate, comingSoon: false },
    { label: 'Incident Documentation Quality', value: data.avg_notes_quality != null ? data.avg_notes_quality * 100 : 0, comingSoon: false },
  ];
}

// ---------- Component ----------
export default function ClientAnalytics() {
  // Queries
  const trendsQ = useQuery({
    queryKey: ['client', 'analytics', 'performance-trends'],
    queryFn: () => clientAnalyticsApi.getPerformanceTrends().then((r) => r.data),
  });

  const issuesQ = useQuery({
    queryKey: ['client', 'analytics', 'issues-by-day'],
    queryFn: () => clientAnalyticsApi.getIssuesByDay().then((r) => r.data),
  });

  const complianceQ = useQuery({
    queryKey: ['client', 'analytics', 'compliance-breakdown'],
    queryFn: () => clientAnalyticsApi.getComplianceBreakdown().then((r) => r.data),
  });

  const topIssuesQ = useQuery({
    queryKey: ['client', 'analytics', 'top-issues'],
    queryFn: () => clientAnalyticsApi.getTopIssues().then((r) => r.data),
  });

  const sitePerformanceQ = useQuery({
    queryKey: ['client', 'analytics', 'site-performance'],
    queryFn: () => clientAnalyticsApi.getSitePerformance().then((r) => r.data),
  });

  // Export handler
  const handleExport = useCallback(() => {
    const sections: string[] = [];

    // Performance Trends
    if (trendsQ.data && trendsQ.data.length > 0) {
      sections.push('Performance Trends');
      sections.push('week_start,entity_type,gg_score,count');
      for (const item of trendsQ.data) {
        sections.push(`${item.week_start},${item.entity_type},${toGGScore(item.avg_score)},${item.count}`);
      }
      sections.push('');
    }

    // Issues by Day
    if (issuesQ.data && issuesQ.data.length > 0) {
      sections.push('Issues by Day');
      sections.push('date,count');
      for (const item of issuesQ.data) {
        sections.push(`${item.date},${item.count}`);
      }
      sections.push('');
    }

    // Quality Metrics
    if (complianceQ.data) {
      const c = complianceQ.data;
      sections.push('Quality Metrics');
      sections.push('metric,value');
      sections.push(`total_shifts,${c.total_shifts}`);
      sections.push(`detailed_notes_rate,${c.detailed_notes_rate}`);
      sections.push(`avg_notes_quality,${c.avg_notes_quality ?? ''}`);
      sections.push('');
    }

    // Top Issues
    if (topIssuesQ.data && topIssuesQ.data.length > 0) {
      sections.push('Top Issues');
      sections.push('rule_name,category,severity,trigger_count,total_impact');
      for (const item of topIssuesQ.data) {
        sections.push(`"${item.rule_name}",${item.category},${item.severity},${item.trigger_count},${toGGDelta(item.total_impact)}`);
      }
      sections.push('');
    }

    // Site Performance
    if (sitePerformanceQ.data && sitePerformanceQ.data.length > 0) {
      sections.push('Site Performance');
      sections.push('site_name,vendor_name,gg_score,total_patrols,total_incidents,dar_submission_rate,total_shifts');
      for (const item of sitePerformanceQ.data) {
        sections.push(`"${item.site_name}","${item.vendor_name ?? ''}",${toGGScore(item.current_score)},${item.total_patrols},${item.total_incidents},${item.dar_submission_rate},${item.total_shifts}`);
      }
    }

    downloadCsv('client-analytics-report.csv', sections.join('\n'));
  }, [trendsQ.data, issuesQ.data, complianceQ.data, topIssuesQ.data, sitePerformanceQ.data]);

  // Derived data
  const trendChartData = trendsQ.data ? transformTrends(trendsQ.data) : [];
  const complianceMetrics = buildComplianceMetrics(complianceQ.data ?? null);

  return (
    <>
      <PageHeader
        title="Analytics"
        description="Performance analytics and reports"
        actions={
          <div className="flex gap-2">
            <button
              onClick={handleExport}
              className="inline-flex items-center gap-2 rounded-xl border-2 border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3M3 17v3a2 2 0 002 2h14a2 2 0 002-2v-3" />
              </svg>
              Export CSV
            </button>
            <button
              onClick={() => window.print()}
              className="inline-flex items-center gap-2 rounded-xl bg-brand-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-brand-700 transition-colors"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
              </svg>
              Export PDF
            </button>
          </div>
        }
      />

      <div className="space-y-8">
        {/* Section 1: Performance Trends */}
        <div className="rounded-2xl border border-slate-100 bg-white p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Performance Trends</h2>
          <p className="text-sm text-slate-500 mb-4">Weekly score trends across your guards, sites, and overall vendor performance.</p>
          {trendsQ.isLoading ? (
            <div className="flex justify-center py-12"><LoadingSpinner /></div>
          ) : !trendsQ.data || trendsQ.data.length === 0 ? (
            <EmptyState title="No trend data" description="Performance trend data will appear after scoring cycles run." />
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={trendChartData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="clientGradVendor" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#2563eb" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#2563eb" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="clientGradSite" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="clientGradGuard" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="week" tick={{ fontSize: 11, fill: '#94a3b8' }} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} />
                <YAxis domain={[300, 900]} tick={{ fontSize: 11, fill: '#94a3b8' }} tickLine={false} axisLine={false} ticks={[300, 450, 600, 750, 900]} />
                <Tooltip contentStyle={TOOLTIP_STYLE} />
                <Legend />
                <Area type="monotone" dataKey="vendor" name="Vendor" stroke="#2563eb" strokeWidth={2} fill="url(#clientGradVendor)" dot={{ r: 3 }} connectNulls />
                <Area type="monotone" dataKey="site" name="Site" stroke="#10b981" strokeWidth={2} fill="url(#clientGradSite)" dot={{ r: 3 }} connectNulls />
                <Area type="monotone" dataKey="guard" name="Guard" stroke="#f59e0b" strokeWidth={2} fill="url(#clientGradGuard)" dot={{ r: 3 }} connectNulls />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Section 2: Issues by Day */}
        <div className="rounded-2xl border border-slate-100 bg-white p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Issues by Day</h2>
          <p className="text-sm text-slate-500 mb-4">Number of rule triggers per day showing when issues were detected.</p>
          {issuesQ.isLoading ? (
            <div className="flex justify-center py-12"><LoadingSpinner /></div>
          ) : !issuesQ.data || issuesQ.data.length === 0 ? (
            <EmptyState title="No issues recorded" description="Issue data will appear as rule triggers are logged." />
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={issuesQ.data.map((d) => ({ ...d, date: formatDate(d.date) }))} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#94a3b8' }} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} />
                <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip contentStyle={TOOLTIP_STYLE} />
                <Bar dataKey="count" name="Issues" fill="#2563eb" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Section 3: Quality Metrics */}
        <div className="rounded-2xl border border-slate-100 bg-white p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Quality Metrics</h2>
          <p className="text-sm text-slate-500 mb-4">Quality indicators based on report detail and incident documentation.</p>
          {complianceQ.isLoading ? (
            <div className="flex justify-center py-12"><LoadingSpinner /></div>
          ) : !complianceQ.data ? (
            <EmptyState title="No compliance data" description="Compliance metrics will appear after shifts are recorded." />
          ) : (
            <div className="space-y-4">
              {complianceMetrics.map((metric) => (
                <div key={metric.label}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-slate-700">{metric.label}</span>
                    {metric.comingSoon ? (
                      <span className="text-xs font-medium text-slate-400">Coming Soon</span>
                    ) : (
                      <span className="text-sm font-semibold text-slate-900">{metric.value.toFixed(1)}%</span>
                    )}
                  </div>
                  <div className="w-full h-2.5 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${metric.comingSoon ? 'bg-slate-200' : barColor(metric.value)}`}
                      style={{ width: `${metric.comingSoon ? 0 : Math.min(metric.value, 100)}%` }}
                    />
                  </div>
                </div>
              ))}
              <p className="text-xs text-slate-400 mt-2">Based on {complianceQ.data.total_shifts} total shifts</p>
            </div>
          )}
        </div>

        {/* Section 4: Top Issues */}
        <div className="rounded-2xl border border-slate-100 bg-white p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Top Issues</h2>
          <p className="text-sm text-slate-500 mb-4">Most frequently triggered rules and their cumulative score impact.</p>
          {topIssuesQ.isLoading ? (
            <div className="flex justify-center py-12"><LoadingSpinner /></div>
          ) : !topIssuesQ.data || topIssuesQ.data.length === 0 ? (
            <EmptyState title="No issues found" description="Top issues will appear after rule triggers are recorded." />
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-100">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-slate-400 uppercase">#</th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-slate-400 uppercase">Rule</th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-slate-400 uppercase">Category</th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-slate-400 uppercase">Severity</th>
                    <th className="px-4 py-2 text-right text-xs font-semibold text-slate-400 uppercase">Triggers</th>
                    <th className="px-4 py-2 text-right text-xs font-semibold text-slate-400 uppercase">Total Impact</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {topIssuesQ.data.map((issue, idx) => (
                    <tr key={issue.rule_id}>
                      <td className="px-4 py-3 text-sm font-medium text-slate-500">{idx + 1}</td>
                      <td className="px-4 py-3 text-sm font-medium text-slate-900">{issue.rule_name}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${categoryVariant(issue.category)}`}>
                          {issue.category}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${severityVariant(issue.severity)}`}>
                          {issue.severity}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm font-semibold text-slate-900 text-right">{issue.trigger_count}</td>
                      <td className="px-4 py-3 text-right">
                        <span className={`text-sm font-semibold ${issue.total_impact >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          {issue.total_impact >= 0 ? '+' : ''}{toGGDelta(issue.total_impact)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Section 5: Site Performance (with vendor_name column) */}
        <div className="rounded-2xl border border-slate-100 bg-white p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-4">Site Performance</h2>
          <p className="text-sm text-slate-500 mb-4">Performance summary for each site including scores, patrols, and incidents.</p>
          {sitePerformanceQ.isLoading ? (
            <div className="flex justify-center py-12"><LoadingSpinner /></div>
          ) : !sitePerformanceQ.data || sitePerformanceQ.data.length === 0 ? (
            <EmptyState title="No site performance data" description="Site performance data will appear after scoring cycles." />
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-100">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-slate-400 uppercase">Site</th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-slate-400 uppercase">Vendor</th>
                    <th className="px-4 py-2 text-right text-xs font-semibold text-slate-400 uppercase">Score</th>
                    <th className="px-4 py-2 text-right text-xs font-semibold text-slate-400 uppercase">Patrols</th>
                    <th className="px-4 py-2 text-right text-xs font-semibold text-slate-400 uppercase">Incidents</th>
                    <th className="px-4 py-2 text-right text-xs font-semibold text-slate-400 uppercase">DAR Rate</th>
                    <th className="px-4 py-2 text-right text-xs font-semibold text-slate-400 uppercase">Shifts</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {sitePerformanceQ.data.map((site) => (
                    <tr key={site.site_id}>
                      <td className="px-4 py-3 text-sm font-medium text-slate-900">{site.site_name}</td>
                      <td className="px-4 py-3 text-sm text-slate-700">{site.vendor_name ?? '-'}</td>
                      <td className="px-4 py-3 text-right">
                        <span className={`text-sm font-bold ${scoreColor(site.current_score)}`}>{toGGScore(site.current_score)}</span>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700 text-right">{site.total_patrols}</td>
                      <td className="px-4 py-3 text-sm text-slate-700 text-right">{site.total_incidents}</td>
                      <td className="px-4 py-3 text-sm text-slate-700 text-right">{site.dar_submission_rate.toFixed(1)}%</td>
                      <td className="px-4 py-3 text-sm text-slate-700 text-right">{site.total_shifts}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
